/*
* MockBookViewr, BookViewer에서 퀴즈 채팅창 또는 질의 채팅창으로 사용하는 컴포넌트
*
*/


import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import { aiLearningService } from '../../services/aiLearningService';
import apiClient from '../../../core/http/axios';
import { quizImageService, QuizItem as ApiQuizItem } from '../../services/quizImageService';
import { useSyncSocketContext } from '../../contexts/SyncSocketContext';
import { dispatchLearningRecordsInvalidate } from '../../contexts/LearningRecordsCacheContext';
import { useRecording } from '../../pages/BookViewer/hooks/useRecording';
import RecordingModalComponent from '../../pages/BookViewer/components/RecordingModalComponent';

const Panel = styled.div`
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 5px solid transparent;
  background:
    linear-gradient(90deg, #DDD0FF 0%, #E6EAD8 32%, #D3FFD8 66%, #CADCFF 100%) border-box;
  box-shadow: ${props => props.theme.shadows.xl};
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: background-color 0.3s ease, color 0.3s ease;
  color: var(--text-primary);

  @media (max-width: 768px) {
    border-width: 3px;
  }
  @media (max-width: 480px) {
    border-width: 2px;
  }
`;

const PanelContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: ${props => props.theme.spacing.lg};
  background-color: var(--bg-secondary);
  border-radius: ${props => props.theme.borderRadius.md};
`;

const PanelHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.lg};
  padding-bottom: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md} ${props => props.theme.borderRadius.md} 0 0;
  background-color: var(--bg-secondary);
  border-bottom: 1px solid ${props => props.theme.colors.border.de};
  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing.sm};
    padding-top: ${props => props.theme.spacing.xl};
  }
  @media (max-width: 480px) {
    padding: ${props => props.theme.spacing.xs};
    padding-top: ${props => props.theme.spacing.lg};
  }
`;

const ScrollableContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${props => props.theme.spacing.lg} 0;
  border-radius: 0 0 ${props => props.theme.borderRadius.md} ${props => props.theme.borderRadius.md};
  background-color: var(--bg-secondary);
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.gray[300]};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.gray[400]};
  }
`;

const FixedBottomSection = styled.div`
  flex-shrink: 0;
  background-color: var(--bg-secondary);
  position: relative;
  z-index: 10;
  pointer-events: auto;
  transition: background-color 0.3s ease;
`;

const ToggleButton = styled.button`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    opacity: 0.7;
  }
`;

const NewChatButton = styled.button`
  position: relative;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  outline: none;

  &::after {
    content: attr(data-tooltip);
    position: absolute;
    top: 50%;
    right: calc(100% + 8px);
    transform: translateY(-50%);
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
    background: ${props => props.theme.colors.book.color2b};
    color: ${props => props.theme.colors.white};
    font-size: ${props => props.theme.fontSize.sm};
    white-space: nowrap;
    border-radius: ${props => props.theme.borderRadius.lg};
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 50%;
    right: calc(100% + 2px);
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-left: 6px solid ${props => props.theme.colors.book.color2b};
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover::after,
  &:hover::before {
    opacity: 1;
  }

  html.dark-mode & svg {
    filter: invert(1);
  }
`;
const PanelTitle = styled.h3`
  margin: 0;
  color: var(--text-primary);
  font-weight: ${props => props.theme.fontWeight.bold};
  font-size: ${props => props.theme.fontSize.base};
  transition: color 0.3s ease;
  margin-right: auto;
`;

const ChatSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
  @media (max-width: 768px) {
    align-items: center;
  }
`;

const ChatBubble = styled.div`
  background: linear-gradient(92deg, #DDD0FF 0%, #E6EAD8 32.42%, #D3FFD8 66.78%, #CADCFF 100.65%), #F0F0F0;
  background-blend-mode: lighten, normal;
  html.dark-mode & {
    background: linear-gradient(92deg, #3A2D5A 0%, #2D3A28 32.42%, #1E3A20 66.78%, #1E2A4A 100.65%), #2A2A2A;
    background-blend-mode: screen, normal;
  }
  border-radius: 8px 8px 0 8px;
  padding: ${props => props.theme.spacing.md};
  align-self: flex-end;
  max-width: 75%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: background 0.3s ease, border-radius 0.3s ease;
  
  @media (max-width: 768px) {
    max-width: 85%;
  }
`;

const ChatMessageText = styled.p<{ $hasBottomSpacing?: boolean; $isBold?: boolean }>`
  margin: ${props => (props.$hasBottomSpacing ? '0 0 4px 0' : '0')};
  color: ${props => (props.$isBold ? props.theme.colors.book.color1b : props.theme.colors.book.color2b)};
  font-weight: ${props => (props.$isBold ? props.theme.fontWeight.semibold : props.theme.fontWeight.light)};
  font-size: ${props => (props.$hasBottomSpacing ? props.theme.fontSize.sm : props.theme.fontSize.base)};
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  transition: color 0.3s ease;
`;

const ResponseText = styled.p`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 50%;
  color: ${props => props.theme.colors.book.color9b};
  font-size: ${props => props.theme.fontSize.base};
  line-height: 1.4;
  transition: color 0.3s ease;
  align-self: flex-start;
  
  @media (max-width: 768px) {
    align-self: center;
    max-width: 100%;
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: var(--border-primary);
  margin-bottom: ${props => props.theme.spacing['2xl']};
  transition: background-color 0.3s ease;
`;

const ResponseContent = styled.div`
  padding: ${props => props.theme.spacing.md};
  align-self: flex-start;
  max-width: 75%;
  margin-bottom: ${props => props.theme.spacing['5xl']};
  
  @media (max-width: 768px) {
    max-width: 85%;
  }
`;

const ResponseTitle = styled.p`
  font-weight: ${props => props.theme.fontWeight.bold};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const ResponseSubtitle = styled.p`
  font-weight: ${props => props.theme.fontWeight.medium};
  margin-top: ${props => props.theme.spacing.xs};
`;

const ResponseBody = styled.div`
  font-weight: ${props => props.theme.fontWeight.normal};
  font-size: ${props => props.theme.fontSize.base};
  line-height: 1.5;
  color: ${props => props.theme.colors.book.color1b};
  word-break: break-word;
  transition: color 0.3s ease;

  h1 {
    font-size: 1.3em;
    font-weight: 700;
    margin: 12px 0 8px 0;
    color: var(--text-primary);
  }
  h2 {
    font-size: 1.15em;
    font-weight: 700;
    margin: 10px 0 6px 0;
    color: var(--text-primary);
  }
  h3 {
    font-size: 1.05em;
    font-weight: 700;
    margin: 8px 0 4px 0;
    color: var(--text-primary);
  }
  h1:first-child, h2:first-child, h3:first-child {
    margin-top: 0;
  }
  p {
    margin: 0 0 8px 0;
    &:last-child { margin-bottom: 0; }
  }
  strong { font-weight: 400; }
  em { font-style: italic; }
  ul, ol {
    margin: 4px 0 8px 0;
    padding-left: 20px;
  }
  li {
    margin-bottom: 2px;
  }
  a {
    color: #2563eb;
    text-decoration: underline;
    &:hover { color: #1d4ed8; }
  }
  code {
    background: var(--code-bg);
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 0.9em;
  }
  pre {
    background: var(--code-bg);
    padding: 8px 12px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 4px 0 8px 0;
    code {
      background: none;
      padding: 0;
    }
  }
  blockquote {
    border-left: 3px solid var(--border-secondary);
    padding-left: 12px;
    margin: 4px 0 8px 0;
    color: var(--text-tertiary);
  }
  hr {
    border: none;
    border-top: 1px solid var(--border-primary);
    margin: 8px 0;
  }
`;

// 퀴즈 섹션 헤더 (퀴즈 생성 + 폴더 아이콘)
const QuizSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  font-size: ${props => props.theme.fontSize.base};
  font-weight: 600;
  color: ${props => props.theme.colors.book.color2b};
`;

// 퀴즈 아이템 컨테이너 - 가로 배치, 줄바꿈 (스크롤 없음)
const QuizItemsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: ${props => props.theme.spacing.sm};
  width: 100%;
`;

// 개별 퀴즈 아이템 카드
const QuizItemCard = styled.div<{ $selected: boolean }>`
  flex: 1;
  min-width: 320px;
  background: ${props => props.$selected ? 'linear-gradient(98deg, #DDD0FF 9.47%, #E6EAD8 38.39%, #D3FFD8 69.04%, #CADCFF 99.25%), #F0F0F0' : 'var(--bg-secondary)'};
  background-blend-mode: lighten, normal;
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.md};
  cursor: pointer;
  transition: all 0.2s ease;
`;

const QuizItemHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
`;

// N번 문항 오벌 배지
const QuizNumberBadge = styled.div<{ $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
  border-radius: 9999px;
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: 600;
  background: ${props => props.$selected ? props.theme.colors.book.color2b : 'var(--bg-secondary)'};
  color: ${props => props.$selected ? '#ffffff' : props.theme.colors.book.color6b};
  border: 1px solid ${props => props.$selected ? props.theme.colors.book.color2b : props.theme.colors.border.de};
`;

const QuizQuestion = styled.p`
  font-size: 14px;
  line-height: 1.55;
  color: var(--text-primary);
  margin: 0 0 12px 0;
`;

const QuizAnswerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
`;

const QuizAnswerBadge = styled.span<{ $answer: 'O' | 'X' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  background: ${props => props.$answer === 'O' ? '#dcfce7' : '#fef2f2'};
  color: ${props => props.$answer === 'O' ? '#16a34a' : '#dc2626'};
`;

const QuizExplanation = styled.p`
  font-size: 13px;
  color: var(--text-tertiary);
  margin: 0;
  line-height: 1.45;
`;


// 퀴즈 발송 버튼
const QuizSendButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.normal};
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: ${props => props.theme.colors.book.color2b};
  color: var(--bg-secondary);
  display: flex;
  align-items: center;
  gap: 6px;
  outline: none;
  &:disabled {
    opacity: 0.8;
    cursor: not-allowed;
  }

`;

const StudentQuizCard = styled.div`
  flex: 1;
  min-width: 320px;
  background: var(--bg-secondary);
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.md};
`;

const StudentQuizQuestion = styled.h3`
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.normal};
  color: ${props => props.theme.colors.book.color2b};
  line-height: 162%;
  width: 100%;
  text-align: left;
  margin: 0 0 12px 0;
`;

const StudentQuizAnswerGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.lg};
  width: 100%;
  margin-bottom: 12px;
`;

const StudentQuizAnswerBtn = styled.button<{ $variant: 'yes' | 'no'; $active?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: calc(50% - ${props => props.theme.spacing.md} / 2);
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: var(--bg-secondary);
  outline: none;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
  span {
    font-size: ${props => props.theme.fontSize.sm};
    font-weight: ${props => props.theme.fontWeight.bold};
    line-height: 100%;
  }
  strong {
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 ${props => props.theme.spacing.sm};
    height: 20px;
    border-radius: 10px;
    color: white;
    font-size: ${props => props.theme.fontSize.xs};
    font-weight: ${props => props.theme.fontWeight.normal};
  }
  ${props => props.$variant === 'yes' ? `
    ${props.$active ? `
      border-color: #2078DC;
      background-color: #EBF3FF;
    ` : ''}
    &:hover { border-color: #2078DC; }
    span { color: #2078DC; }
    strong { background-color: #2078DC; }
  ` : `
    ${props.$active ? `
      border-color: #D94B4B;
      background-color: #FFF0F0;
    ` : ''}
    &:hover { border-color: #D94B4B; }
    span { color: #D94B4B; }
    strong { background-color: #D94B4B; }
  `}
`;

const StudentQuizSubmitBtn = styled.button`
  width: 100%;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
  cursor: pointer;
  border: none;
  background: ${props => props.theme.colors.book.color2b};
  color: white;
  transition: all 0.2s;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StudentQuizResultLabel = styled.div<{ $correct: boolean }>`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.$correct ? '#16a34a' : '#dc2626'};
  margin-bottom: 8px;
`;

const StudentQuizHint = styled.p`
  font-size: 13px;
  color: var(--text-tertiary);
  margin: 0 0 8px 0;
  line-height: 1.45;
`;

const ResponseList = styled.ul`
  margin-left: ${props => props.theme.spacing['2xl']};
  margin-top: ${props => props.theme.spacing.xs};
  
  li {
    font-weight: ${props => props.theme.fontWeight.light};
    list-style: disc;
  }
`;

// 로딩 스피너 애니메이션
const spinAnimation = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ThinkingIndicator = styled.div`
  ${spinAnimation}
  border-radius: ${props => props.theme.borderRadius.full};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.md};
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  align-self: flex-start;
  border: 3px solid transparent;
  background:
    linear-gradient(var(--bg-secondary), var(--bg-secondary)) padding-box,
    linear-gradient(92deg, #DDD0FF 0%, #E6EAD8 32.42%, #D3FFD8 66.78%, #CADCFF 100.65%) border-box;
  background-size: 100% 100%, 200% 100%;
  animation: borderGradientShift 2s ease-in-out infinite;

  @keyframes borderGradientShift {
    0% { background-position: 0 0, 0% 50%; }
    50% { background-position: 0 0, 100% 50%; }
    100% { background-position: 0 0, 0% 50%; }
  }
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid #E5E5E5;
  border-top: 2px solid #FEE500;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ThinkingText = styled.span`
  color: var(--text-tertiary);
  font-size: ${props => props.theme.fontSize.sm};
`;

const ChatInputSection = styled.div`
  margin-top: ${props => props.theme.spacing.lg};
  border: 1px solid ${props => props.theme.colors.border.de};
  border-radius: ${props => props.theme.borderRadius.sm};
  background-color: var(--bg-secondary);
  position: relative;
  z-index: 1;
  pointer-events: auto;
  transition: background-color 0.3s ease, border-color 0.3s ease;
`;


const ChatInputWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  width: 100%;
  padding: ${props => props.theme.spacing.sm};
`;

const QuickActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 4px;
  }
`;

const QuickActionButton = styled.button<{ $active?: boolean }>`
  background-color: ${props => props.$active ? 'var(--quick-action-active-bg)' : 'var(--quick-action-bg)'};
  border-radius: ${props => props.theme.borderRadius.full};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  color: ${props => props.$active ? props.theme.colors.white : 'var(--text-primary)'};
  outline: none;
  font-weight: ${props => props.theme.fontWeight.medium};
  font-size: ${props => props.theme.fontSize.sm};
  
  &:hover {
    background-color: ${props => props.$active ? 'var(--quick-action-active-hover)' : 'var(--bg-secondary)'};
  }

  &:active {
    background-color: var(--quick-action-active-bg);
    color: ${props => props.theme.colors.white};
    svg path {
      stroke: ${props => props.theme.colors.white};
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    flex-shrink: 0;
    & path {
      stroke: ${props => props.$active ? props.theme.colors.book.color2b : 'var(--text-primary)'};
    }
  }
`;

const SelectedText = styled.div`
  background-color: var(--quick-action-bg);
  border-radius: ${props => props.theme.borderRadius.sm};
  padding: ${props => props.theme.spacing.xs};
  display: flex;     
  gap: ${props => props.theme.spacing.md};
  transition: background-color 0.3s ease;
  button {
    outline: none;
    border: none;
    background-color: transparent;
    cursor: pointer;
    display: inline-flex;
    padding: 4px;
  }
`;

const SelectedTextContent = styled.p`
  flex: 1;
  color: ${props => props.theme.colors.book.color4b};
  font-weight: ${props => props.theme.fontWeight.light};
  font-size: ${props => props.theme.fontSize.sm};
  line-height: 1.4;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${props => props.theme.spacing.xs};
  position: relative;
  z-index: 1;
  border-top: 1px solid ${props => props.theme.colors.border.de};
  padding: ${props => props.theme.spacing.sm};
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  border-top: 1px solid var(--border-secondary);
`;

const Input = styled.textarea`
  flex: 1;
  min-width: 0;
  outline: none;
  background: transparent;
  color: ${props => props.theme.colors.book.color2b};
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.xs};
  min-height: 24px;
  max-height: 120px;
  cursor: text;
  pointer-events: auto;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  resize: none;
  overflow-y: auto;
  line-height: 1.5;
  font-family: inherit;
  
  &:focus {
    outline: none;
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    pointer-events: none;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.gray[400]};
  }
`;

const SendButton = styled.button<{ $disabled?: boolean }>`
  width: 24px;
  height: 24px;
  background-color: ${props => props.$disabled ? props.theme.colors.book.color5b : props.theme.colors.book.color2b};
  border-radius: ${props => props.theme.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  padding: 0;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: opacity 0.2s;
  position: relative;
  outline: none;

  &:hover:not(:disabled) {
    background-color: ${props => props.$disabled ? props.theme.colors.book.color6b : props.theme.colors.book.color2b};
  }

  svg {
    flex-shrink: 0;
  }
`;

const SendButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  transform: translateY(-6px});
`;

const RecordingButton = styled.button<{ $isRecording?: boolean }>`
  display: flex;
  width: 24px;
  height: 24px;
  padding: 4px;
  justify-content: center;
  align-items: center;
  aspect-ratio: 1/1;
  border-radius: 4px;
  border: ${props => props.$isRecording ? '1px solid #D4FDD8' : 'none'};
  background: ${props => props.$isRecording
    ? 'linear-gradient(98deg, #DDD0FF 9.47%, #E6EAD8 38.39%, #D3FFD8 69.04%, #CADCFF 99.25%), #F0F0F0'
    : 'transparent'};
  background-blend-mode: ${props => props.$isRecording ? 'lighten, normal' : 'normal'};
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    aspect-ratio: 1/1;
  }
`;

// 이미지 업로드 관련 스타일
const ImageUploadButton = styled.button`
  width: 24px;
  height: 24px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.7;
  }
`;

const ImagePreviewContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
  max-height: 120px;
  overflow-y: auto;
`;

const ImagePreviewItem = styled.div`
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e5e5;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  background-color: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
  }
`;

// 채팅 버블 내 이미지 스타일
const ChatImageGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
`;

const ChatImage = styled.img`
  max-width: 100px;
  max-height: 100px;
  border-radius: 8px;
  object-fit: cover;
  cursor: pointer;
  
  &:hover {
    opacity: 0.9;
  }
`;

// 업로드된 이미지 정보 타입
export interface UploadedImage {
  file: File;
  preview: string;
  uploadedUrl?: string;
}

// 퀴즈 아이템 타입
export interface QuizItem {
  id: string;
  questionNumber: number;  // Q1, Q2, Q3...
  question: string;        // 문제 내용
  answer: string;          // O 또는 X
  explanation: string;     // 정답 설명
  selected: boolean;       // 선택 여부
}

// ChatMessage 타입 export
export interface ChatMessage {
  id: string;
  type: 'user' | 'ai';
  content: string;
  selectedText?: string;
  images?: string[]; // 업로드된 이미지 URL 배열
  timestamp: Date;
  quizItems?: QuizItem[];  // 퀴즈 응답인 경우 파싱된 퀴즈 아이템들
}

interface AILearningPanelProps {
  onToggle: () => void;
  selectedText?: string;
  onClearSelectedText?: () => void;
  bookUid?: string;
  messages: ChatMessage[];
  onMessagesChange: (messages: ChatMessage[]) => void;
  dialogNo: number;
  onDialogNoChange: (dialogNo: number) => void;
  mode?: 'chat' | 'quiz';
  pageNumber?: number;  // 현재 페이지 번호
  dialogUuid?: string;  // 다이얼로그 UUID (무한 스크롤용)
  hasMoreChats?: boolean;  // 더 불러올 chats가 있는지 여부
  onLoadMoreChats?: () => Promise<void>;  // 추가 chats 로드 함수
  onNewChat?: () => void;  // 새 채팅하기 콜백
  chatTitle?: string;  // 학습 기록 카드에서 진입 시 표시할 제목 (없으면 'AI 채팅')
  isProfessor?: boolean;
}

/**
 * API 응답의 QuizItem을 내부 QuizItem 형식으로 변환
 */
const convertApiQuizToLocalQuiz = (apiQuiz: ApiQuizItem, index: number): QuizItem => {
  return {
    id: apiQuiz.quiz_uuid || `quiz-${Date.now()}-${index}`,
    questionNumber: apiQuiz.quiz_no,
    question: apiQuiz.question,
    answer: apiQuiz.correct_answer,
    explanation: apiQuiz.description,
    selected: false,
  };
};

/**
 * 퀴즈 응답을 파싱하여 QuizItem 배열로 변환
 * 형식: "Q1. 문제내용 정답: O/X, 설명" 또는 "Q1. 문제내용\n정답: O/X\n설명"
 * (레거시 지원용 - 새로운 API는 사용하지 않음)
 */
const parseQuizResponse = (content: string): QuizItem[] | null => {
  // Q로 시작하는 패턴이 있는지 확인
  if (!content.match(/Q\d+[.．]/)) {
    return null;
  }

  const quizItems: QuizItem[] = [];
  
  // Q1. Q2. Q3. 등으로 분리 (전각/반각 마침표 모두 지원)
  const quizPattern = /Q(\d+)[.．]\s*([\s\S]*?)(?=Q\d+[.．]|$)/g;
  let match;

  while ((match = quizPattern.exec(content)) !== null) {
    const questionNumber = parseInt(match[1], 10);
    const quizContent = match[2].trim();
    
    // 정답과 설명 파싱
    // 패턴: "문제내용 정답: O/X, 설명" 또는 "문제내용\n정답: O/X\n설명"
    const answerMatch = quizContent.match(/(.+?)\s*정답[：:]\s*([OXox×○])[,，\s]?\s*([\s\S]*)/);
    
    if (answerMatch) {
      const question = answerMatch[1].trim();
      const answer = answerMatch[2].toUpperCase() === 'O' || answerMatch[2] === '○' ? 'O' : 'X';
      const explanation = answerMatch[3].trim();
      
      quizItems.push({
        id: `quiz-${Date.now()}-${questionNumber}`,
        questionNumber,
        question,
        answer,
        explanation,
        selected: false, // 기본적으로 선택 안됨
      });
    }
  }

  return quizItems.length > 0 ? quizItems : null;
};

const AILearningPanel: React.FC<AILearningPanelProps> = ({
  onToggle,
  selectedText,
  onClearSelectedText,
  bookUid,
  messages,
  onMessagesChange,
  dialogNo,
  onDialogNoChange,
  mode = 'chat',
  pageNumber = 1,
  dialogUuid,
  hasMoreChats = false,
  onLoadMoreChats,
  onNewChat,
  chatTitle,
  isProfessor = false,
}) => {
  // Context에서 직접 소켓 정보 가져오기
  const { isSyncSocketConnected, sendQuizPublish } = useSyncSocketContext();
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMoreChats, setLoadingMoreChats] = useState(false);
  const [currentSelectedText, setCurrentSelectedText] = useState<string>('');
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [quizSetUuid, setQuizSetUuid] = useState<string | null>(null);
  const [quizSetRevNo, setQuizSetRevNo] = useState<number | null>(null);
  const [isExtractingText, setIsExtractingText] = useState(false);
  const [studentQuizAnswers, setStudentQuizAnswers] = useState<Record<string, 'O' | 'X' | null>>({});
  const [studentQuizResults, setStudentQuizResults] = useState<Record<string, boolean>>({});
  const savedInputValueRef = useRef<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollableContentRef = useRef<HTMLDivElement>(null);
  
  // 음성 녹음 훅 (auto_create_lr: false, showTitleInput: false)
  const recordingHook = useRecording(bookUid, pageNumber?.toString(), {
    autoCreateLr: false,
    showTitleInput: false,
  });

  // selectedText props가 변경되면 currentSelectedText에만 설정 (inputValue에는 설정하지 않음)
  React.useEffect(() => {
    if (selectedText) {
      setCurrentSelectedText(selectedText);
      // inputValue에는 설정하지 않음 (선택된 텍스트는 별도 영역에 표시)
    } else {
      // selectedText가 없으면 currentSelectedText도 초기화
      setCurrentSelectedText('');
    }
  }, [selectedText]);

  // inputValue가 변경될 때마다 textarea 높이 자동 조절
  React.useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(textareaRef.current.scrollHeight, 120);
      textareaRef.current.style.height = newHeight + 'px';
    }
  }, [inputValue]);

  // 메시지가 추가되거나 로딩 상태가 변경될 때 스크롤을 맨 아래로 이동
  // 단, 이전 메시지를 로드하는 중(loadingMoreChats)이 아닐 때만
  React.useEffect(() => {
    if (scrollableContentRef.current && !loadingMoreChats) {
      // 다음 틱에서 스크롤 이동 (DOM 업데이트 후)
      setTimeout(() => {
        if (scrollableContentRef.current) {
          scrollableContentRef.current.scrollTop = scrollableContentRef.current.scrollHeight;
        }
      }, 0);
    }
  }, [messages, isLoading, loadingMoreChats]);

  // 스크롤 핸들러 (무한 스크롤)
  const handleScroll = React.useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    // 상단에 가까울 때 이전 메시지 로드 (채팅은 최신이 아래에 있으므로)
    if (target.scrollTop < 100 && hasMoreChats && !loadingMoreChats && !isLoading && onLoadMoreChats && dialogUuid) {
      // 메시지 추가 전의 스크롤 위치와 높이 저장
      const previousScrollHeight = target.scrollHeight;
      const previousScrollTop = target.scrollTop;
      
      setLoadingMoreChats(true);
      onLoadMoreChats().finally(() => {
        setLoadingMoreChats(false);
        // DOM 업데이트 후 스크롤 위치 조정
        setTimeout(() => {
          if (scrollableContentRef.current) {
            const newScrollHeight = scrollableContentRef.current.scrollHeight;
            const heightDifference = newScrollHeight - previousScrollHeight;
            // 높이 차이만큼 스크롤 위치를 조정하여 현재 보던 위치 유지
            scrollableContentRef.current.scrollTop = previousScrollTop + heightDifference;
          }
        }, 0);
      });
    }
  }, [hasMoreChats, loadingMoreChats, isLoading, onLoadMoreChats, dialogUuid]);

  // 이미지 선택 핸들러
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newImages: UploadedImage[] = [];
    Array.from(files).forEach((file) => {
      if (file.type.startsWith('image/')) {
        const preview = URL.createObjectURL(file);
        newImages.push({ file, preview });
      }
    });

    setUploadedImages((prev) => [...prev, ...newImages]);
    
    // input 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 이미지 제거 핸들러
  const handleRemoveImage = (index: number) => {
    setUploadedImages((prev) => {
      const newImages = [...prev];
      // 미리보기 URL 해제
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  // 이미지 업로드 API 호출
  const uploadImages = async (images: UploadedImage[]): Promise<string[]> => {
    const uploadedUrls: string[] = [];
    
    for (const image of images) {
      try {
        const formData = new FormData();
        formData.append('upload', image.file);
        formData.append('purpose', 'GENERAL');
        
        const response = await apiClient.post('/viewer/files/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        if (response.data?.url) {
          uploadedUrls.push(response.data.url);
        } else {
          // 업로드 성공했지만 URL이 없는 경우 로컬 프리뷰 사용
          uploadedUrls.push(image.preview);
        }
      } catch (error) {
        // console.error('이미지 업로드 실패:', error);
        // 실패 시 로컬 프리뷰 사용
        uploadedUrls.push(image.preview);
      }
    }
    
    return uploadedUrls;
  };

  const handleClearSelectedText = () => {
    setCurrentSelectedText('');
    onClearSelectedText?.();
  };

  // 퀴즈 아이템 선택/해제 토글 (그룹 단위)
  const handleToggleQuizItem = (messageId: string, quizId: string) => {
    const updatedMessages = messages.map(msg => {
      if (msg.id === messageId && msg.quizItems) {
        // 클릭한 퀴즈의 현재 상태 확인
        const clickedQuiz = msg.quizItems.find(quiz => quiz.id === quizId);
        const willBeSelected = clickedQuiz ? !clickedQuiz.selected : false;
        
        return {
          ...msg,
          quizItems: msg.quizItems.map(quiz => 
            quiz.id === quizId ? { ...quiz, selected: willBeSelected } : quiz
          ),
        };
      } else if (msg.quizItems) {
        // 다른 그룹의 모든 퀴즈 선택 해제
        return {
          ...msg,
          quizItems: msg.quizItems.map(quiz => ({ ...quiz, selected: false })),
        };
      }
      return msg;
    });
    onMessagesChange(updatedMessages);
  };

  // 선택된 퀴즈만 다시 생성 (새 채팅 메시지로 추가)
  const handleRegenerateSelectedQuizzes = async () => {
    if (!bookUid) return;
    
    // 가장 최근 퀴즈 메시지 찾기 (중복 방지)
    let latestQuizMessage: ChatMessage | null = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].quizItems && messages[i].quizItems!.length > 0) {
        latestQuizMessage = messages[i];
        break;
      }
    }
    
    if (!latestQuizMessage || !latestQuizMessage.quizItems) {
      return;
    }
    
    // 가장 최근 메시지의 퀴즈만 사용
    const allQuizzes = latestQuizMessage.quizItems;
    const selectedQuizUuids = allQuizzes
      .filter(quiz => quiz.selected)
      .map(quiz => quiz.id);
    
    if (selectedQuizUuids.length === 0) {
      return;
    }
    
    // input 값 저장 (초기화 전에)
    const inputText = inputValue.trim();
    
    setIsLoading(true);
    
    // 선택된 퀴즈 번호 문자열 생성 (UI 표시용)
    const selectedQuizzes = allQuizzes.filter(q => selectedQuizUuids.includes(q.id));
    const quizNumbersText = selectedQuizzes.map(q => `Q${q.questionNumber}`).join(', ');
    
    // 사용자 메시지: input 값이 있으면 사용, 없으면 자동 생성
    const userMessageContent = inputText || (
      selectedQuizzes.length === 1 
        ? `${quizNumbersText} 문제를 다시 생성해줘`
        : `${quizNumbersText} 문제들을 다시 생성해줘`
    );
    
    // 사용자 메시지 추가
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: userMessageContent,
      timestamp: new Date(),
    };
    onMessagesChange([...messages, userMessage]);
    
    // input 초기화
    setInputValue('');
    
    try {
      // 내부 QuizItem을 API QuizItem 형식으로 변환 (API 스펙 순서에 맞게)
      const apiQuizzes: ApiQuizItem[] = allQuizzes.map(quiz => ({
        quiz_uuid: quiz.id,
        question: quiz.question,
        correct_answer: quiz.answer as 'O' | 'X',
        description: quiz.explanation,
        hint: '',
        difficulty: 'medium',
        scope: 'entire_book_pages',
        quiz_no: quiz.questionNumber,
      }));
      
      // subject는 input에 입력된 텍스트 사용
      const subjectValue = inputText || '';
      
      // 퀴즈 교체 API 호출
      if (!quizSetUuid) {
        throw new Error('퀴즈 세트 UUID가 없습니다. 먼저 퀴즈를 생성해주세요.');
      }
      
      const quizzes = await quizImageService.replaceQuizzes(bookUid, {
        set_uuid: quizSetUuid,
        subject: subjectValue,
        page_idx: pageNumber,
        quizzes: apiQuizzes,
        replace_targets: selectedQuizUuids,
      });
      
      // API 응답을 내부 QuizItem 형식으로 변환
      const quizItems = quizzes.map((quiz, index) => convertApiQuizToLocalQuiz(quiz, index));
      
      // 퀴즈 목록을 텍스트로 변환 (메시지 표시용)
      const quizContent = quizItems.map((quiz, idx) => 
        `Q${quiz.questionNumber}. ${quiz.question}\n정답: ${quiz.answer}\n${quiz.explanation}`
      ).join('\n\n');
      
      // AI 응답 메시지 추가 + 기존 퀴즈 체크박스 해제
      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: quizContent,
        timestamp: new Date(),
        quizItems: quizItems,
      };
      
      // 기존 메시지의 체크박스를 해제하고 새 메시지 추가
      const updatedMessages = messages.map(msg => {
        if (msg.quizItems) {
          return {
            ...msg,
            quizItems: msg.quizItems.map(quiz => ({ ...quiz, selected: false })),
          };
        }
        return msg;
      });
      onMessagesChange([...updatedMessages, userMessage, aiMessage]);
    } catch (error) {
      // console.error('퀴즈 재생성 실패:', error);
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: '퀴즈 재생성에 실패했습니다. 다시 시도해주세요.',
        timestamp: new Date(),
      };
      onMessagesChange([...messages, userMessage, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // 퀴즈가 존재하는지 확인
  const hasQuizzes = (): boolean => {
    return messages.some(msg => msg.quizItems && msg.quizItems.length > 0);
  };

  // 모든 퀴즈 체크박스 해제
  const clearAllQuizSelections = () => {
    const updatedMessages = messages.map(msg => {
      if (msg.quizItems) {
        return {
          ...msg,
          quizItems: msg.quizItems.map(quiz => ({ ...quiz, selected: false })),
        };
      }
      return msg;
    });
    onMessagesChange(updatedMessages);
  };

  // 선택된 퀴즈 발송
  const handleSendSelectedQuizzes = () => {
    // console.log('[퀴즈 발송] 시작', { 
    //   isSyncSocketConnected, 
    //   hasSendQuizPublish: !!sendQuizPublish 
    // });
    
    const selectedQuizzes: QuizItem[] = [];
    messages.forEach(msg => {
      if (msg.quizItems) {
        msg.quizItems.forEach(quiz => {
          if (quiz.selected) {
            selectedQuizzes.push(quiz);
          }
        });
      }
    });
    
    if (selectedQuizzes.length === 0) {
      alert('발송할 퀴즈를 선택해주세요.');
      return;
    }
    
    // console.log('[퀴즈 발송] 선택된 퀴즈:', selectedQuizzes.length, '개');
    
    // 소켓을 통해 퀴즈 발송
    if (!sendQuizPublish) {
      // console.error('[퀴즈 발송] sendQuizPublish 함수가 없습니다.');
      alert('동기화 소켓이 연결되어 있지 않습니다. 먼저 동기화를 시작해주세요.');
      return;
    }
    
    if (!isSyncSocketConnected) {
      // console.error('[퀴즈 발송] 소켓이 연결되어 있지 않습니다.', { isSyncSocketConnected });
      alert('동기화 소켓이 연결되어 있지 않습니다. 먼저 동기화를 시작해주세요.');
      return;
    }
    
    if (!quizSetUuid) {
      alert('퀴즈 세트 정보가 없습니다. 먼저 퀴즈를 생성해주세요.');
      return;
    }

    if (!quizSetRevNo) {
      alert('퀴즈 세트 리비전 정보가 없습니다. 먼저 퀴즈를 생성해주세요.');
      return;
    }

    try {
      // QuizItem 전체를 전송 (id, questionNumber 등 포함)
      // console.log('[퀴즈 발송] 전송할 데이터:', {
      //   quizzes: selectedQuizzes,
      //   set_uuid: quizSetUuid,
      //   set_rev_no: quizSetRevNo,
      // });
      sendQuizPublish(selectedQuizzes, quizSetUuid, quizSetRevNo);
      // console.log('[퀴즈 발송] 전송 완료');
      alert(`${selectedQuizzes.length}개의 퀴즈를 발송했습니다.`);
      
      // 발송 후 체크박스 해제
      clearAllQuizSelections();
    } catch (error) {
      // console.error('[퀴즈 발송] 오류 발생:', error);
      alert('퀴즈 발송 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  // 선택된 퀴즈 개수 계산
  const getSelectedQuizCount = (): number => {
    let count = 0;
    messages.forEach(msg => {
      if (msg.quizItems) {
        msg.quizItems.forEach(quiz => {
          if (quiz.selected) count++;
        });
      }
    });
    return count;
  };

  const handleSendMessage = async (overrideMessage?: string) => {
    if (overrideMessage) {
      setInputValue(overrideMessage);
    }
    const currentInput = overrideMessage ?? inputValue;
    const hasContent = mode === 'quiz' 
      ? (currentInput.trim() || uploadedImages.length > 0)
      : currentInput.trim();
    
    if (!hasContent || isLoading) {
      return;
    }
    
    // console.log('[AILearningPanel] 메시지 전송 시작:', { mode, hasImages: uploadedImages.length > 0 });
    if (!bookUid) {
      // console.error('bookUid가 없습니다.');
      return;
    }

    const message = currentInput.trim();
    
    setIsLoading(true);
    
    // 퀴즈 모드 + 이미지가 있는 경우: 텍스트 추출 로직
    if (mode === 'quiz' && uploadedImages.length > 0) {
      // 이미지 프리뷰 URL 저장 (메시지에 표시용)
      const imagePreviewUrls = uploadedImages.map(img => img.preview);
      
      const currentInput = inputValue;
      const currentSelectedTextForSubject = currentSelectedText; // 초기화 전에 저장
      
      // 사용자 메시지 추가 (오른쪽) - content(입력 텍스트)와 selectedText(도서 텍스트) 분리
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'user',
        content: message || '이미지로 퀴즈 생성해줘',
        selectedText: currentSelectedTextForSubject || undefined,
        images: imagePreviewUrls,
        timestamp: new Date(),
      };
      onMessagesChange([...messages, userMessage]);
      
      // 이미지 파일 배열 추출 (초기화 전에)
      const files = uploadedImages.map(img => img.file);
      
      // 입력값과 이미지 초기화 (처리 시작 전에)
      setInputValue('');
      setCurrentSelectedText('');
      setUploadedImages([]);
      
      try {
        
        // 퀴즈 이미지 처리 (업로드 + 텍스트 추출)
        const imageProcessResult = await quizImageService.processQuizImages(files, bookUid, (current, total, status) => {
          // console.log(`[Quiz] 진행 상황: ${status}`);
        });
        
        if (imageProcessResult.success && imageProcessResult.extractedTexts.length > 0) {
          // 모든 텍스트가 추출되었을 때 콘솔에 출력 (운영 배포 시 주석 처리)
          // console.log('='.repeat(60));
          // console.log('[퀴즈 생성용 추출 데이터]');
          // console.log('='.repeat(60));
          // console.log('사용자 입력 메시지:', currentInput || '(없음)');
          // console.log('선택된 텍스트:', currentSelectedTextForSubject || '(없음)');
          // console.log('추출된 텍스트 개수:', imageProcessResult.extractedTexts.length);
          // imageProcessResult.extractedTexts.forEach((text, idx) => {
          //   console.log(`\n[이미지 ${idx + 1} 추출 텍스트]`);
          //   console.log('-'.repeat(40));
          //   console.log(text);
          // });
          // console.log('='.repeat(60));
          // console.log('[전체 추출 텍스트 (합친 버전)]');
          // console.log('-'.repeat(40));
          const combinedText = imageProcessResult.extractedTexts.join('\n\n---\n\n');
          // console.log(combinedText);
          // console.log('='.repeat(60));
          
          try {
            // 새로운 퀴즈 생성 API 호출
            // 모든 input 데이터를 JSON으로 만들어서 subject에 담기
            const allInputData = {
              inputValue: currentInput.trim() || '',
              selectedText: currentSelectedTextForSubject.trim() || '',
              extractedTexts: imageProcessResult.extractedTexts,
              pageNumber: pageNumber,
            };
            const subjectValue = JSON.stringify(allInputData);
            const createResult = await quizImageService.createQuizzes(bookUid, {
              subject: subjectValue,
              page_idx: pageNumber,
            });
            
            // set_uuid와 set_rev_no 저장
            setQuizSetUuid(createResult.setUuid);
            setQuizSetRevNo(createResult.setRevNo);
            
            // API 응답을 내부 QuizItem 형식으로 변환
            const quizItems = createResult.quizzes.map((quiz, index) => convertApiQuizToLocalQuiz(quiz, index));
            
            // 퀴즈 목록을 텍스트로 변환 (메시지 표시용)
            const quizContent = quizItems.map((quiz, idx) => 
              `Q${quiz.questionNumber}. ${quiz.question}\n정답: ${quiz.answer}\n${quiz.explanation}`
            ).join('\n\n');
            
            // AI 응답 메시지 추가
            const aiMessage: ChatMessage = {
              id: (Date.now() + 1).toString(),
              type: 'ai',
              content: quizContent,
              timestamp: new Date(),
              quizItems: quizItems,
            };
            onMessagesChange([...messages, userMessage, aiMessage]);
            dispatchLearningRecordsInvalidate(bookUid);
          } catch (quizError) {
            // console.error('퀴즈 채팅 API 실패:', quizError);
            // 텍스트 추출은 성공했지만 퀴즈 생성 실패
            const errorMessage: ChatMessage = {
              id: (Date.now() + 1).toString(),
              type: 'ai',
              content: `텍스트 추출은 완료되었지만, 퀴즈 생성에 실패했습니다.\n다시 시도해주세요.`,
              timestamp: new Date(),
            };
            onMessagesChange([...messages, userMessage, errorMessage]);
          }
        } else {
          // 추출 실패
          const errorMessage: ChatMessage = {
            id: (Date.now() + 1).toString(),
            type: 'ai',
            content: `❌ 이미지에서 텍스트 추출에 실패했습니다.\n실패: ${imageProcessResult.failedCount}개\n\n다시 시도해주세요.`,
            timestamp: new Date(),
          };
          onMessagesChange([...messages, userMessage, errorMessage]);
        }
      } catch (error) {
        // console.error('퀴즈 이미지 처리 실패:', error);
        const errorMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          type: 'ai',
          content: '이미지 처리 중 오류가 발생했습니다. 다시 시도해주세요.',
          timestamp: new Date(),
        };
        onMessagesChange([...messages, userMessage, errorMessage]);
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    // 퀴즈 모드에서 텍스트만 있는 경우: 선택된 퀴즈 여부에 따라 분기
    if (mode === 'quiz' && message) {
      // 선택된 퀴즈 확인
      const selectedQuizCount = getSelectedQuizCount();
      
      // 선택된 퀴즈가 있으면 다시 생성 (replace API)
      if (selectedQuizCount > 0) {
        handleRegenerateSelectedQuizzes();
        return;
      }
      
      // 선택된 퀴즈가 없으면 신규 생성 (create API)
      // API 호출 전에 값들을 저장 (초기화 전에)
      const inputText = inputValue.trim();
      const selectedTextForSubject = currentSelectedText.trim();
      
      // 사용자 메시지 추가 (오른쪽) - content(입력 텍스트)와 selectedText(도서 텍스트) 분리
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'user',
        content: inputText,
        selectedText: selectedTextForSubject || undefined,
        timestamp: new Date(),
      };
      onMessagesChange([...messages, userMessage]);
      
      setInputValue('');
      
      try {
        // 새로운 퀴즈 생성 API 호출
        // 모든 input 데이터를 JSON으로 만들어서 subject에 담기
        const allInputData = {
          inputValue: inputText.trim() || '',
          selectedText: selectedTextForSubject.trim() || '',
          pageNumber: pageNumber,
        };
        const subjectValue = JSON.stringify(allInputData);
        const result = await quizImageService.createQuizzes(bookUid, {
          subject: subjectValue,
          page_idx: pageNumber,
        });
        
        // set_uuid와 set_rev_no 저장
        setQuizSetUuid(result.setUuid);
        setQuizSetRevNo(result.setRevNo);
        
        // API 응답을 내부 QuizItem 형식으로 변환
        const quizItems = result.quizzes.map((quiz, index) => convertApiQuizToLocalQuiz(quiz, index));
        
        // 퀴즈 목록을 텍스트로 변환 (메시지 표시용)
        const quizContent = quizItems.map((quiz, idx) => 
          `Q${quiz.questionNumber}. ${quiz.question}\n정답: ${quiz.answer}\n${quiz.explanation}`
        ).join('\n\n');
        
        // AI 응답 메시지 추가
        const aiMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          type: 'ai',
          content: quizContent,
          timestamp: new Date(),
          quizItems: quizItems,
        };
        onMessagesChange([...messages, userMessage, aiMessage]);
        dispatchLearningRecordsInvalidate(bookUid);
        setCurrentSelectedText('');
        handleClearSelectedText();
      } catch (error) {
        // console.error('퀴즈 생성 실패:', error);
        const errorMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          type: 'ai',
          content: '퀴즈 생성에 실패했습니다. 다시 시도해주세요.',
          timestamp: new Date(),
        };
        onMessagesChange([...messages, userMessage, errorMessage]);
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    // 채팅 모드: 기존 로직
    const hasSelectedText = currentSelectedText && currentSelectedText.trim();

    // API 전송용 query: 학생 메시지 + 빈 줄 + 선택한 도서 텍스트
    const selectedValue = hasSelectedText ? currentSelectedText!.trim() : '';
    const inputValueForQuery = message || '';
    const queryForApi = selectedValue
      ? `${inputValueForQuery}\n\n${selectedValue}`
      : inputValueForQuery;
    
    // 이미지가 있으면 먼저 업로드
    let imageUrls: string[] = [];
    if (uploadedImages.length > 0) {
      imageUrls = await uploadImages(uploadedImages);
    }
    
    // 사용자 메시지 추가 (오른쪽) - content(입력 텍스트)와 selectedText(도서 텍스트) 분리
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: message,
      selectedText: hasSelectedText ? currentSelectedText : undefined,
      images: imageUrls.length > 0 ? imageUrls : undefined,
      timestamp: new Date(),
    };
    onMessagesChange([...messages, userMessage]);
    
    setInputValue('');
    // 선택된 텍스트는 전송 성공 후에만 제거
    // 업로드된 이미지 초기화
    uploadedImages.forEach((img) => URL.revokeObjectURL(img.preview));
    setUploadedImages([]);

    try {
      const response = await aiLearningService.sendMessage({
        query: queryForApi,
        book_uuid: bookUid,
        dialogNo: dialogNo,
        page_no: Math.max(1, pageNumber || 1), // 페이지 번호 추가 (최소 1)
        mode: 'mix', // AI 모델 선택 (mix: 믹스, rag: RAG, llm: LLM)
      });
      
      // dialog_no 업데이트 (다음 요청에 사용)
      onDialogNoChange(response.dialogNo);
      
      // AI 응답 메시지 추가 (왼쪽)
      const responseContent = response?.message || '답변을 받지 못했습니다.';
      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: responseContent,
        timestamp: new Date(),
      };
      onMessagesChange([...messages, userMessage, aiMessage]);
      if (bookUid) dispatchLearningRecordsInvalidate(bookUid);
      handleClearSelectedText();
    } catch (error) {
      // console.error('AI 학습 메시지 전송 실패:', error);
      // 에러 메시지 추가
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: '메시지 전송에 실패했습니다. 다시 시도해주세요.',
        timestamp: new Date(),
      };
      onMessagesChange([...messages, userMessage, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
    // Shift+Enter는 기본 동작(줄바꿈) 허용
  };

  // 음성 녹음 업로드 핸들러 (텍스트 추출 후 input에 넣기)
  const handleRecordingUpload = async () => {
    try {
      const result = await recordingHook.uploadRecording();
      
      // auto_create_lr이 false인 경우 job_uuid를 받아서 텍스트 추출 폴링
      if (result && 'jobUuid' in result) {
        setIsExtractingText(true);
        
        try {
          // 텍스트 추출 폴링
          const extractionResult = await quizImageService.pollTextExtraction(result.jobUuid);
          
          if (extractionResult.status === 'DONE' && extractionResult.resultText) {
            // 추출된 텍스트를 input 영역에 넣기
            setInputValue(prev => {
              const newValue = prev.trim() 
                ? `${prev.trim()}\n${extractionResult.resultText}` 
                : extractionResult.resultText || '';
              // 다음 틱에서 높이 조절
              setTimeout(() => {
                if (textareaRef.current) {
                  textareaRef.current.style.height = 'auto';
                  const newHeight = Math.min(textareaRef.current.scrollHeight, 120);
                  textareaRef.current.style.height = newHeight + 'px';
                }
              }, 0);
              return newValue;
            });
            
            // 텍스트 추출 성공 후 모달 닫기
            recordingHook.closeRecordingModal();
            recordingHook.resetRecording();
          } else {
            alert('텍스트 추출에 실패했습니다.');
          }
        } catch (error) {
          // console.error('텍스트 추출 실패:', error);
          alert('텍스트 추출 중 오류가 발생했습니다.');
        } finally {
          setIsExtractingText(false);
        }
      }
    } catch (error) {
      // console.error('녹음 업로드 실패:', error);
    }
  };

  // 인라인 녹음 토글: 버튼 클릭 → 바로 녹음 시작, 다시 클릭 → 녹음 종료 + 텍스트 변환
  const handleInlineRecordingToggle = async () => {
    if (isExtractingText) return;

    if (recordingHook.isRecording || recordingHook.isPaused) {
      // 녹음 종료 → 저장된 입력값 복원
      const restored = savedInputValueRef.current;
      setInputValue(restored);
      savedInputValueRef.current = '';
      recordingHook.stopRecording();
      setTimeout(async () => {
        await handleRecordingUpload();
      }, 300);
    } else {
      // 녹음 시작 → 현재 입력값 저장 후 비우기 (placeholder 표시를 위해)
      savedInputValueRef.current = inputValue;
      setInputValue('');
      await recordingHook.startRecordingSilent();
    }
  };

  return (
    <Panel>
      <PanelContent>
        <PanelHeader>
          <PanelTitle>{mode === 'quiz' ? '퀴즈 생성하기' : (chatTitle || 'AI 채팅')}</PanelTitle>
          <NewChatButton onClick={() => {
            setInputValue('');
            setCurrentSelectedText('');
            setUploadedImages(prev => {
              prev.forEach(img => URL.revokeObjectURL(img.preview));
              return [];
            });
            setStudentQuizAnswers({});
            setStudentQuizResults({});
            onClearSelectedText?.();
            onNewChat?.();
          }} data-tooltip="새 채팅하기">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M3.12744 10H16.8736" stroke="#0B0B0B" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M10.0005 3.12891V16.8751" stroke="#0B0B0B" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </NewChatButton>
          <ToggleButton onClick={onToggle} aria-label="접기">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 4L16 16" stroke="var(--text-primary)" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M16 4L4 16" stroke="var(--text-primary)" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </ToggleButton>
        </PanelHeader>

      <ScrollableContent ref={scrollableContentRef} onScroll={handleScroll}>
        {messages.length === 0 && (
          <ResponseText style={{ textAlign: 'center', color: '#999' }}>
            {mode === 'quiz' 
              ? '퀴즈 요청을 입력하세요.'
              : '텍스트를 선택하고 뿍챗 버튼을 눌러 대화를 시작하세요.'}
          </ResponseText>
        )}
        
        <ChatSection>
          {messages.map((msg) => (
            msg.type === 'user' ? (
              <ChatBubble key={msg.id}>
                {msg.content && <ChatMessageText $hasBottomSpacing={!!msg.selectedText} $isBold={!!msg.selectedText}>{msg.content}</ChatMessageText>}
                {msg.selectedText && (
                  <ChatMessageText>{msg.selectedText}</ChatMessageText>
                )}
                {msg.images && msg.images.length > 0 && (
                  <ChatImageGrid>
                    {msg.images.map((imgUrl, idx) => (
                      <ChatImage 
                        key={idx} 
                        src={imgUrl} 
                        alt={`업로드 이미지 ${idx + 1}`}
                        onClick={() => window.open(imgUrl, '_blank')}
                      />
                    ))}
                  </ChatImageGrid>
                )}
              </ChatBubble>
            ) : (
              <ResponseContent key={msg.id}>
                {msg.quizItems && msg.quizItems.length > 0 ? (
                  isProfessor ? (
                  <>
                    <QuizSectionHeader>
                      📘 퀴즈 생성
                    </QuizSectionHeader>
                    <QuizItemsContainer>
                    {msg.quizItems.map((quiz) => (
                      <QuizItemCard 
                        key={quiz.id} 
                        $selected={quiz.selected}
                        onClick={() => handleToggleQuizItem(msg.id, quiz.id)}
                      >
                        <QuizItemHeader>
                          <QuizNumberBadge $selected={quiz.selected}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <path d="M4.95825 7.2915L6.12492 8.45817L9.04159 5.5415" stroke={quiz.selected ? "#fff" : "#6B6B6B"} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M4.08329 1.94701C4.96964 1.4341 5.97591 1.16482 6.99996 1.16651C10.2217 1.16651 12.8333 3.7781 12.8333 6.99985C12.8333 10.2216 10.2217 12.8332 6.99996 12.8332C3.77821 12.8332 1.16663 10.2216 1.16663 6.99985C1.16663 5.9376 1.45071 4.94068 1.94713 4.08318" stroke={quiz.selected ? "#fff" : "#6B6B6B"} strokeWidth="1.2" strokeLinecap="round"/>
                            </svg>
                            {quiz.questionNumber}번 문항
                          </QuizNumberBadge>
                        </QuizItemHeader>
                        <QuizQuestion>{quiz.question}</QuizQuestion>
                        <QuizAnswerRow>
                          정답 : <QuizAnswerBadge $answer={quiz.answer as 'O' | 'X'}>{quiz.answer}</QuizAnswerBadge>
                        </QuizAnswerRow>
                        <QuizExplanation>{quiz.explanation}</QuizExplanation>
                      </QuizItemCard>
                    ))}
                  </QuizItemsContainer>
                  </>
                  ) : (
                  <>
                    <QuizSectionHeader>
                      📘 퀴즈 풀기
                    </QuizSectionHeader>
                    <QuizItemsContainer>
                    {msg.quizItems.map((quiz) => {
                      const answered = studentQuizResults[quiz.id] !== undefined;
                      const selectedAnswer = studentQuizAnswers[quiz.id] ?? null;
                      const correctAnswer = quiz.answer as 'O' | 'X';
                      const isCorrect = selectedAnswer === correctAnswer;
                      return (
                        <StudentQuizCard key={quiz.id}>
                          <QuizItemHeader>
                            <QuizNumberBadge $selected={false}>
                              Q{quiz.questionNumber}
                            </QuizNumberBadge>
                          </QuizItemHeader>
                          <StudentQuizQuestion>{quiz.question}</StudentQuizQuestion>
                          {!answered ? (
                            <>
                              <StudentQuizAnswerGroup>
                                <StudentQuizAnswerBtn
                                  $variant="yes"
                                  $active={selectedAnswer === 'O'}
                                  onClick={() => setStudentQuizAnswers(prev => ({ ...prev, [quiz.id]: 'O' }))}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="29" viewBox="0 0 42 44" fill="none">
                                    <path d="M0 21.1968C0 9.0432 7.3728 0 20.9664 0C34.56 0 41.9328 9.0432 41.9328 21.1968C41.9328 32.8896 35.136 43.4304 20.9664 43.4304C6.1056 43.4304 0 33.7536 0 21.1968ZM9.4464 21.1968C9.4464 29.1456 12.096 35.8272 20.9664 35.8272C29.5488 35.8272 32.4864 29.1456 32.4864 21.1968C32.4864 13.248 28.6848 7.6032 20.9664 7.6032C13.248 7.6032 9.4464 13.248 9.4464 21.1968Z" fill="#2078DC"/>
                                  </svg>
                                  <span>그렇다</span>
                                </StudentQuizAnswerBtn>
                                <StudentQuizAnswerBtn
                                  $variant="no"
                                  $active={selectedAnswer === 'X'}
                                  onClick={() => setStudentQuizAnswers(prev => ({ ...prev, [quiz.id]: 'X' }))}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 42 42" fill="none">
                                    <path d="M1.59789 34.019L14.0905 20.0895L2.68737 7.04799C1.96105 6.21555 1.45263 5.32761 1.45263 4.27319C1.45263 1.88686 3.99474 0 7.40842 0C10.2411 0 11.6211 1.27641 12.3474 2.21984L20.9905 13.5965L30.2147 2.21984C31.4495 0.721448 32.4663 0 35.0811 0C37.9863 0 40.8189 1.22091 40.8189 4.1067C40.8189 4.88365 40.3105 5.93807 39.7295 6.54853L27.6 20.0895L39.4389 33.6861C40.6737 35.1289 41.4 35.8504 41.4 36.9048C41.4 39.8461 38.64 41.4 35.2989 41.4C33.3379 41.4 31.6674 41.067 30.2874 39.2911L20.7 27.0265L10.3863 39.6241C9.36947 40.9005 7.55368 41.4 5.73789 41.4C2.25158 41.4 0 39.7351 0 37.5153C0 36.5718 0.217895 35.5729 1.59789 34.019Z" fill="#D94B4B"/>
                                  </svg>
                                  <span>아니다</span>
                                </StudentQuizAnswerBtn>
                              </StudentQuizAnswerGroup>
                              <StudentQuizSubmitBtn
                                disabled={selectedAnswer === null}
                                onClick={() => {
                                  if (selectedAnswer !== null) {
                                    setStudentQuizResults(prev => ({ ...prev, [quiz.id]: selectedAnswer === correctAnswer }));
                                  }
                                }}
                              >
                                정답확인
                              </StudentQuizSubmitBtn>
                            </>
                          ) : (
                            <>
                              <StudentQuizResultLabel $correct={isCorrect}>
                                {isCorrect ? '정답입니다!' : '오답입니다'}
                              </StudentQuizResultLabel>
                              <StudentQuizAnswerGroup>
                                <StudentQuizAnswerBtn
                                  $variant="yes"
                                  $active={correctAnswer === 'O'}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="29" viewBox="0 0 42 44" fill="none">
                                    <path d="M0 21.1968C0 9.0432 7.3728 0 20.9664 0C34.56 0 41.9328 9.0432 41.9328 21.1968C41.9328 32.8896 35.136 43.4304 20.9664 43.4304C6.1056 43.4304 0 33.7536 0 21.1968ZM9.4464 21.1968C9.4464 29.1456 12.096 35.8272 20.9664 35.8272C29.5488 35.8272 32.4864 29.1456 32.4864 21.1968C32.4864 13.248 28.6848 7.6032 20.9664 7.6032C13.248 7.6032 9.4464 13.248 9.4464 21.1968Z" fill="#2078DC"/>
                                  </svg>
                                  <span>그렇다</span>
                                  {correctAnswer === 'O' && <strong>정답</strong>}
                                </StudentQuizAnswerBtn>
                                <StudentQuizAnswerBtn
                                  $variant="no"
                                  $active={correctAnswer === 'X'}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 42 42" fill="none">
                                    <path d="M1.59789 34.019L14.0905 20.0895L2.68737 7.04799C1.96105 6.21555 1.45263 5.32761 1.45263 4.27319C1.45263 1.88686 3.99474 0 7.40842 0C10.2411 0 11.6211 1.27641 12.3474 2.21984L20.9905 13.5965L30.2147 2.21984C31.4495 0.721448 32.4663 0 35.0811 0C37.9863 0 40.8189 1.22091 40.8189 4.1067C40.8189 4.88365 40.3105 5.93807 39.7295 6.54853L27.6 20.0895L39.4389 33.6861C40.6737 35.1289 41.4 35.8504 41.4 36.9048C41.4 39.8461 38.64 41.4 35.2989 41.4C33.3379 41.4 31.6674 41.067 30.2874 39.2911L20.7 27.0265L10.3863 39.6241C9.36947 40.9005 7.55368 41.4 5.73789 41.4C2.25158 41.4 0 39.7351 0 37.5153C0 36.5718 0.217895 35.5729 1.59789 34.019Z" fill="#D94B4B"/>
                                  </svg>
                                  <span>아니다</span>
                                  {correctAnswer === 'X' && <strong>정답</strong>}
                                </StudentQuizAnswerBtn>
                              </StudentQuizAnswerGroup>
                              {quiz.explanation && (
                                <StudentQuizHint>{quiz.explanation}</StudentQuizHint>
                              )}
                            </>
                          )}
                        </StudentQuizCard>
                      );
                    })}
                  </QuizItemsContainer>
                  </>
                  )
                ) : (
                <ResponseBody><ReactMarkdown>{msg.content}</ReactMarkdown></ResponseBody>
                )}
              </ResponseContent>
            )
          ))}
        </ChatSection>
        
        {isLoading && (
          <ThinkingIndicator>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7.5834 5.20457L4.62124 2.24299C4.46561 2.08443 4.28011 1.95827 4.07545 1.87182C3.87079 1.78536 3.65103 1.74031 3.42886 1.73928C3.20669 1.73824 2.98652 1.78123 2.78106 1.86578C2.5756 1.95032 2.38893 2.07473 2.23183 2.23183C2.07473 2.38893 1.95032 2.5756 1.86578 2.78106C1.78123 2.98652 1.73824 3.20669 1.73928 3.42886C1.74031 3.65103 1.78536 3.87079 1.87182 4.07545C1.95827 4.28011 2.08443 4.46561 2.24299 4.62124L9.3789 11.7577C9.69616 12.0644 10.1212 12.2341 10.5624 12.2303C11.0036 12.2266 11.4257 12.0496 11.7376 11.7376C12.0496 11.4257 12.2266 11.0036 12.2303 10.5624C12.2341 10.1212 12.0644 9.69616 11.7577 9.3789L9.96223 7.5834M3.50007 5.8334L5.8334 3.50007" stroke="#6B6B6B" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M9.39143 1.3459C9.41204 1.29304 9.44812 1.24764 9.49496 1.21564C9.5418 1.18363 9.5972 1.1665 9.65393 1.1665C9.71066 1.1665 9.76607 1.18363 9.8129 1.21564C9.85974 1.24764 9.89582 1.29304 9.91643 1.3459L10.1673 1.98465C10.1812 2.02082 10.2025 2.05371 10.2298 2.08122C10.2571 2.10873 10.2899 2.13028 10.3259 2.14448L10.9623 2.39648C11.0151 2.41747 11.0602 2.4538 11.0921 2.50075C11.1239 2.5477 11.1409 2.60313 11.1409 2.65986C11.1409 2.71659 11.1239 2.77201 11.0921 2.81896C11.0602 2.86592 11.0151 2.90224 10.9623 2.92323L10.3265 3.17523C10.2907 3.1899 10.2582 3.21153 10.2309 3.23887C10.2036 3.2662 10.1819 3.29871 10.1673 3.33448L9.91643 3.97265C9.89582 4.0255 9.85974 4.0709 9.8129 4.10291C9.76607 4.13492 9.71066 4.15204 9.65393 4.15204C9.5972 4.15204 9.5418 4.13492 9.49496 4.10291C9.44812 4.0709 9.41204 4.0255 9.39143 3.97265L9.1406 3.3339C9.12593 3.29812 9.1043 3.26562 9.07696 3.23828C9.04963 3.21094 9.01712 3.18931 8.98135 3.17465L8.34552 2.92265C8.29281 2.90165 8.24762 2.86533 8.21578 2.81838C8.18394 2.77143 8.16692 2.716 8.16692 2.65927C8.16692 2.60254 8.18394 2.54712 8.21578 2.50016C8.24762 2.45321 8.29281 2.41689 8.34552 2.3959L8.98135 2.1439C9.01718 2.12917 9.04971 2.10745 9.07705 2.08C9.1044 2.05256 9.126 2.01995 9.1406 1.98406L9.39143 1.3459ZM11.6472 5.32598C11.6678 5.27313 11.7039 5.22773 11.7507 5.19572C11.7975 5.16371 11.853 5.14659 11.9097 5.14659C11.9664 5.14659 12.0218 5.16371 12.0687 5.19572C12.1155 5.22773 12.1516 5.27313 12.1722 5.32598L12.2632 5.55873C12.2923 5.63165 12.3495 5.6894 12.4224 5.71798L12.6546 5.81015C12.7071 5.83124 12.7521 5.86756 12.7838 5.91445C12.8155 5.96133 12.8325 6.01663 12.8325 6.07323C12.8325 6.12983 12.8155 6.18513 12.7838 6.23201C12.7521 6.2789 12.7071 6.31523 12.6546 6.33631L12.4224 6.42848C12.3867 6.44315 12.3542 6.46478 12.3268 6.49212C12.2995 6.51945 12.2778 6.55196 12.2632 6.58773L12.1722 6.82106C12.1516 6.87392 12.1155 6.91932 12.0687 6.95133C12.0218 6.98333 11.9664 7.00046 11.9097 7.00046C11.853 7.00046 11.7975 6.98333 11.7507 6.95133C11.7039 6.91932 11.6678 6.87392 11.6472 6.82106L11.5556 6.58773C11.541 6.55201 11.5195 6.51954 11.4922 6.49221C11.465 6.46487 11.4326 6.44321 11.3969 6.42848L11.1648 6.33631C11.1122 6.31523 11.0672 6.2789 11.0355 6.23201C11.0038 6.18513 10.9869 6.12983 10.9869 6.07323C10.9869 6.01663 11.0038 5.96133 11.0355 5.91445C11.0672 5.86756 11.1122 5.83124 11.1648 5.81015L11.3969 5.71798C11.4326 5.70325 11.465 5.68159 11.4922 5.65426C11.5195 5.62692 11.541 5.59445 11.5556 5.55873L11.6472 5.32598ZM2.99402 8.92923C3.01462 8.87638 3.05071 8.83098 3.09754 8.79897C3.14438 8.76696 3.19979 8.74984 3.25652 8.74984C3.31324 8.74984 3.36865 8.76696 3.41549 8.79897C3.46232 8.83098 3.49841 8.87638 3.51902 8.92923L3.6106 9.16256C3.62461 9.19863 3.64594 9.2314 3.67325 9.25881C3.70056 9.28622 3.73325 9.30767 3.76927 9.32181L4.00143 9.4134C4.05413 9.43439 4.09933 9.47071 4.13117 9.51766C4.163 9.56462 4.18002 9.62004 4.18002 9.67677C4.18002 9.7335 4.163 9.78893 4.13117 9.83588C4.09933 9.88283 4.05413 9.91916 4.00143 9.94015L3.76927 10.0323C3.73325 10.0465 3.70056 10.0679 3.67325 10.0953C3.64594 10.1227 3.62461 10.1555 3.6106 10.1916L3.51902 10.4249C3.49841 10.4777 3.46232 10.5232 3.41549 10.5552C3.36865 10.5872 3.31324 10.6043 3.25652 10.6043C3.19979 10.6043 3.14438 10.5872 3.09754 10.5552C3.05071 10.5232 3.01462 10.4777 2.99402 10.4249L2.90243 10.1916C2.88842 10.1555 2.86709 10.1227 2.83978 10.0953C2.81247 10.0679 2.77978 10.0465 2.74377 10.0323L2.5116 9.94015C2.4589 9.91916 2.4137 9.88283 2.38187 9.83588C2.35003 9.78893 2.33301 9.7335 2.33301 9.67677C2.33301 9.62004 2.35003 9.56462 2.38187 9.51766C2.4137 9.47071 2.4589 9.43439 2.5116 9.4134L2.74377 9.32181C2.77984 9.30761 2.81257 9.28607 2.83988 9.25855C2.86719 9.23104 2.88849 9.19816 2.90243 9.16198L2.99402 8.92923Z" fill="#6B6B6B"/>
            </svg>
            <ThinkingText>좋은 답변을 생각하는 중 ...</ThinkingText>
          </ThinkingIndicator>
        )}
        {loadingMoreChats && (
          <ThinkingIndicator>
            <Spinner />
            <ThinkingText>이전 메시지를 불러오는 중...</ThinkingText>
          </ThinkingIndicator>
        )}
      </ScrollableContent>
      
      {/* 2번 영역: 하단 고정 */}
      <FixedBottomSection>
        <ChatInputSection>
          <ChatInputWrap>
            {/* 선택된 텍스트 표시 (채팅 모드와 퀴즈 모드 모두) */}
            {currentSelectedText && (
              <SelectedText>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M11.375 7.00016L8.45833 9.91683M11.375 7.00016L8.45833 4.0835M11.375 7.00016H7.58333M5.54167 7.00016C4.56925 7.00016 2.625 6.41683 2.625 4.0835" stroke="#4B4B4B" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <SelectedTextContent>
                  {currentSelectedText.length > 100 
                    ? currentSelectedText.substring(0, 100) + '...' 
                    : currentSelectedText}
                </SelectedTextContent>
                <button onClick={handleClearSelectedText} type="button">
                  <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                    <path d="M9.5 0.5L0.5 9.5" stroke="#2B2B2B" strokeLinecap="round" />
                    <path d="M0.5 0.5L9.5 9.5" stroke="#2B2B2B" strokeLinecap="round" />
                  </svg>
                </button>
              </SelectedText>
            )}

            {/* 빠른 액션 버튼: 항상 표시 */}
            <QuickActions>
                {mode === 'quiz' ? (
                  <>
                    {isProfessor && getSelectedQuizCount() > 0 && (
                      <QuizSendButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSendSelectedQuizzes();
                        }}
                        type="button"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M12.424 10.4468C11.6173 12.8668 11.2133 14.0775 10.622 14.4215C10.0593 14.7481 9.36401 14.7481 8.80201 14.4215C8.21001 14.0775 7.80668 12.8675 7.00001 10.4468C6.87001 10.0581 6.80535 9.86412 6.69668 9.70146C6.59095 9.54434 6.4558 9.40918 6.29868 9.30345C6.13601 9.19479 5.94201 9.13012 5.55335 9.00079C3.13335 8.19412 1.92268 7.79012 1.57868 7.19879C1.41816 6.92226 1.33362 6.6082 1.33362 6.28845C1.33362 5.96871 1.41816 5.65465 1.57868 5.37812C1.92268 4.78679 3.13335 4.38345 5.55335 3.57612M13.3507 7.66679L13.568 7.01346C14.568 4.01479 15.068 2.51545 14.276 1.72412C13.484 0.932788 11.9853 1.43212 8.98601 2.43212L8.28201 2.66679M11.86 4.14012L9.05268 6.91679" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        퀴즈 발송 ({getSelectedQuizCount()})
                      </QuizSendButton>
                    )}
                  </>
                ) : (
                  <>
                    <QuickActionButton disabled={recordingHook.isRecording || isLoading} onClick={() => {
                      handleSendMessage('내용을 요약 정리해줘');
                    }}>
                      <svg width="12" height="13.333" viewBox="0 0 13 15" fill="none">
                        <path d="M12.5 9.83314C12.5 11.7191 12.5 12.6618 11.914 13.2478C11.3287 13.8331 10.3853 13.8331 8.5 13.8331H4.5C2.61467 13.8331 1.67133 13.8331 1.086 13.2478C0.5 12.6611 0.5 11.7191 0.5 9.83314V7.83314M9.16667 1.83447C10.6167 1.84247 11.402 1.90714 11.914 2.41914C12.5 3.00514 12.5 3.94781 12.5 5.83314V7.16647M3.83333 1.83447C2.38333 1.84247 1.598 1.90714 1.086 2.41914C0.574 2.93114 0.509333 3.71647 0.501333 5.16647M4.5 10.8331H8.5" stroke="#2B2B2B" strokeLinecap="round"/>
                        <path d="M3.83301 1.5C3.83301 1.23478 3.93836 0.98043 4.1259 0.792893C4.31344 0.605357 4.56779 0.5 4.83301 0.5H8.16634C8.43156 0.5 8.68591 0.605357 8.87345 0.792893C9.06098 0.98043 9.16634 1.23478 9.16634 1.5V2.16667C9.16634 2.43188 9.06098 2.68624 8.87345 2.87377C8.68591 3.06131 8.43156 3.16667 8.16634 3.16667H4.83301C4.56779 3.16667 4.31344 3.06131 4.1259 2.87377C3.93836 2.68624 3.83301 2.43188 3.83301 2.16667V1.5Z" stroke="#2B2B2B"/>
                        <path d="M3.83366 8.49984H4.50033M9.16699 8.49984H6.50033M9.83366 6.1665H8.50033M6.50033 6.1665H3.16699" stroke="#2B2B2B" strokeLinecap="round"/>
                      </svg>
                      스마트 요약
                    </QuickActionButton>
                    <QuickActionButton disabled={recordingHook.isRecording || isLoading} onClick={() => {
                      handleSendMessage('키워드를 풀이해줘');
                    }}>
                      <svg width="12.667" height="13.333" viewBox="0 0 14 15" fill="none">
                        <path d="M13.1667 9.83333C13.1667 11.7187 13.1667 12.662 12.5807 13.2473C11.9953 13.8333 11.052 13.8333 9.16667 13.8333H5.16667C3.28133 13.8333 2.338 13.8333 1.75267 13.2473C1.16667 12.662 1.16667 11.7187 1.16667 9.83333V4.5C1.16667 2.61467 1.16667 1.67133 1.75267 1.086C2.338 0.5 3.28133 0.5 5.16667 0.5H9.16667C11.052 0.5 11.9953 0.5 12.5807 1.086C13.1667 1.67133 13.1667 2.61467 13.1667 4.5V7.16667M4.5 0.5V3.16667M4.5 13.8333V5.83333M0.5 7.16667H1.83333M0.5 9.83333H1.83333M0.5 4.5H1.83333M6.83333 3.5H10.1667M6.83333 5.83333H10.1667" stroke="#2B2B2B" strokeLinecap="round"/>
                      </svg>
                      키워드 풀이
                    </QuickActionButton>
                    {/*
                    <QuickActionButton onClick={() => {
                      setInputValue('');
                      setCurrentSelectedText('');
                      onClearSelectedText?.();
                      onNewChat?.();
                    }}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M1.33301 8.00016C1.33301 11.1428 1.33301 12.7142 2.30901 13.6902C3.28634 14.6668 4.85701 14.6668 7.99967 14.6668C11.1423 14.6668 12.7137 14.6668 13.6897 13.6902C14.6663 12.7148 14.6663 11.1428 14.6663 8.00016V7.00016M8.99967 1.3335H7.99967C4.85701 1.3335 3.28567 1.3335 2.30901 2.3095C1.66034 2.95883 1.44234 3.8715 1.36967 5.3335" stroke="#2B2B2B" strokeLinecap="round"/>
                        <path d="M11.1015 2.30358L11.5341 1.87091C11.8784 1.52675 12.3452 1.33343 12.832 1.3335C13.3188 1.33356 13.7856 1.52699 14.1298 1.87124C14.474 2.21549 14.6673 2.68236 14.6672 3.16915C14.6671 3.65593 14.4737 4.12275 14.1295 4.46691L13.6961 4.89958C13.6961 4.89958 12.7775 4.84558 11.9668 4.03424C11.1555 3.22358 11.1015 2.30424 11.1015 2.30424L7.12479 6.28024C6.85546 6.54958 6.72079 6.68424 6.60479 6.83291C6.4679 7.00846 6.35146 7.19691 6.25546 7.39824C6.17479 7.56824 6.11479 7.74891 5.99412 8.11024L5.60812 9.26691M5.60812 9.26691L5.35879 10.0149C5.32947 10.1021 5.32506 10.1958 5.34607 10.2854C5.36707 10.375 5.41266 10.457 5.47771 10.5221C5.54275 10.5872 5.62467 10.6329 5.71425 10.654C5.80383 10.6751 5.89752 10.6708 5.98479 10.6416L6.73346 10.3922M5.60812 9.26691L6.73346 10.3922M13.6968 4.89891L11.7081 6.88691M9.72012 8.87558C9.45079 9.14491 9.31612 9.27958 9.16746 9.39558C8.99222 9.53216 8.80267 9.64929 8.60212 9.74491C8.43212 9.82558 8.25146 9.88558 7.89012 10.0062L6.73346 10.3922" stroke="#2B2B2B" strokeLinecap="round"/>
                      </svg>
                      새 채팅하기
                    </QuickActionButton>
                    */}
                  </>
                )}
              </QuickActions>
            
            {/* 퀴즈 모드에서 이미지 미리보기 */}
            {mode === 'quiz' && uploadedImages.length > 0 && (
              <ImagePreviewContainer>
                {uploadedImages.map((image, index) => (
                  <ImagePreviewItem key={index}>
                    <PreviewImage src={image.preview} alt={`미리보기 ${index + 1}`} />
                    <RemoveImageButton onClick={() => handleRemoveImage(index)}>
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M1 1L9 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                        <path d="M9 1L1 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </RemoveImageButton>
                  </ImagePreviewItem>
                ))}
              </ImagePreviewContainer>
            )}
          </ChatInputWrap>  
            
          <InputContainer
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {/* 퀴즈 모드에서 이미지 업로드 버튼 - 주석처리 */}
            {/* {mode === 'quiz' && (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                />
                <ImageUploadButton
                  onClick={() => fileInputRef.current?.click()}
                  type="button"
                  aria-label="이미지 업로드"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <rect x="2" y="2" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
                    <circle cx="6" cy="6" r="1.5" fill="#2B2B2B" />
                    <path d="M2 12L6 8L10 12L14 8L16 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </ImageUploadButton>
              </>
            )} */}
            <Input
              ref={textareaRef}
              rows={1}
              placeholder={
                recordingHook.isRecording ? '음성을 듣는중입니다...'
                : mode === 'quiz' ? '퀴즈 요청을 입력하세요...'
                : '질문을 입력하세요...'
              }
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                // 자동 높이 조절
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
              onKeyDown={handleKeyPress}
              disabled={isLoading || isExtractingText || recordingHook.isRecording}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
            {/* 전송 버튼 또는 ... 버튼 */}
            {(() => {
              const hasContent = mode === 'quiz' 
                ? (inputValue.trim() || uploadedImages.length > 0)
                : inputValue.trim();
              const isTextSendDisabled = isLoading || isExtractingText || !hasContent;
              const isButtonDisabled = isLoading || isExtractingText;
              
              // 채팅 모드이거나 input이 있으면 항상 ... 버튼 표시
              // 퀴즈 모드에서 input이 없고 이미지도 없으면 일반 전송 버튼 (비활성화)
              if (mode === 'chat' || hasContent) {
                return (
                  <SendButtonsWrapper>
                    <RecordingButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleInlineRecordingToggle();
                      }}
                      disabled={isExtractingText}
                      $isRecording={recordingHook.isRecording}
                      aria-label="음성 녹음"
                      title={recordingHook.isRecording ? '녹음 중지 및 텍스트 변환' : '음성 녹음'}
                      type="button"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M4.66699 5.33333C4.66699 4.44928 5.01818 3.60143 5.6433 2.97631C6.26842 2.35119 7.11627 2 8.00033 2C8.88438 2 9.73223 2.35119 10.3573 2.97631C10.9825 3.60143 11.3337 4.44928 11.3337 5.33333V7.33333C11.3337 8.21739 10.9825 9.06523 10.3573 9.69036C9.73223 10.3155 8.88438 10.6667 8.00033 10.6667C7.11627 10.6667 6.26842 10.3155 5.6433 9.69036C5.01818 9.06523 4.66699 8.21739 4.66699 7.33333V5.33333Z" stroke="#4B4B4B"/>
                        <path d="M8.66667 5.3335H11.3333M8.66667 7.3335H11.3333M8 12.6668V14.6668" stroke="#4B4B4B" strokeLinecap="round"/>
                        <path d="M13.8337 6.6665C13.8337 6.5339 13.781 6.40672 13.6872 6.31295C13.5934 6.21918 13.4663 6.1665 13.3337 6.1665C13.2011 6.1665 13.0739 6.21918 12.9801 6.31295C12.8863 6.40672 12.8337 6.5339 12.8337 6.6665H13.8337ZM3.16699 6.6665C3.16699 6.5339 3.11431 6.40672 3.02055 6.31295C2.92678 6.21918 2.7996 6.1665 2.66699 6.1665C2.53438 6.1665 2.40721 6.21918 2.31344 6.31295C2.21967 6.40672 2.16699 6.5339 2.16699 6.6665H3.16699ZM10.3417 11.5625C10.2257 11.6269 10.14 11.7347 10.1035 11.8622C10.067 11.9897 10.0826 12.1265 10.147 12.2425C10.2114 12.3585 10.3192 12.4442 10.4467 12.4807C10.5742 12.5172 10.711 12.5015 10.827 12.4372L10.3417 11.5625ZM12.8337 6.6665V7.33317H13.8337V6.6665H12.8337ZM3.16699 7.33317V6.6665H2.16699V7.33317H3.16699ZM8.00033 12.1665C6.71845 12.1665 5.48907 11.6573 4.58264 10.7509C3.67622 9.84443 3.16699 8.61505 3.16699 7.33317H2.16699C2.16699 8.88027 2.78157 10.364 3.87554 11.458C4.9695 12.5519 6.45323 13.1665 8.00033 13.1665V12.1665ZM12.8337 7.33317C12.8338 8.19644 12.6028 9.04399 12.1646 9.78775C11.7263 10.5315 11.0969 11.1443 10.3417 11.5625L10.827 12.4372C11.7382 11.9324 12.4977 11.1928 13.0264 10.2952C13.5552 9.39766 13.8339 8.37489 13.8337 7.33317H12.8337Z" fill="#4B4B4B"/>
                      </svg>
                    </RecordingButton>
                    <SendButton
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isTextSendDisabled) {
                          handleSendMessage();
                        }
                      }}
                      disabled={isTextSendDisabled}
                      $disabled={isTextSendDisabled}
                      aria-label="메시지 전송"
                      title="메시지 전송"
                      type="button"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect width="24" height="24" rx="4" fill="#2B2B2B"/>
                        <path d="M12 7L8 10.75M12 7L16 10.75M12 7V13.5625M12 17V15.4375" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </SendButton>
                  </SendButtonsWrapper>
                );
              } else {
                return (
                  <SendButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSendMessage();
                    }}
                    disabled={isTextSendDisabled}
                    $disabled={isTextSendDisabled}
                    aria-label="메시지 전송"
                    title="메시지 전송"
                    type="button"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {isExtractingText ? (
                      <Spinner style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect width="24" height="24" rx="4" fill="#2B2B2B"/>
                        <path d="M12 7L8 10.75M12 7L16 10.75M12 7V13.5625M12 17V15.4375" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </SendButton>
                );
              }
            })()}
          </InputContainer>
        </ChatInputSection>
      </FixedBottomSection>
      
      {/* 음성 녹음 모달 */}
      <RecordingModalComponent
        isOpen={recordingHook.isRecordingModalOpen}
        micErrorModal={recordingHook.micErrorModal}
        onCloseMicError={recordingHook.closeMicErrorModal}
        isPermissionRequestModalOpen={recordingHook.isPermissionRequestModalOpen}
        onOpenPermissionRequestModal={recordingHook.openPermissionRequestModal}
        onClosePermissionRequestModal={recordingHook.closePermissionRequestModal}
        onConfirmPermissionAndStart={recordingHook.confirmPermissionAndStartRecording}
        isRecording={recordingHook.isRecording}
        isPaused={recordingHook.isPaused}
        recordingTime={recordingHook.recordingTime}
        isUploading={recordingHook.isUploading}
        isPlaying={recordingHook.isPlaying}
        audioChunksLength={recordingHook.audioChunksRef.current.length}
        fileName={recordingHook.fileName}
        setFileName={recordingHook.setFileName}
        baseFileName={recordingHook.baseFileName}
        onStartRecording={recordingHook.startRecordingWithModal}
        onStopRecording={recordingHook.stopRecording}
        onPauseRecording={recordingHook.pauseRecording}
        onResumeRecording={recordingHook.resumeRecording}
        onCancelRecording={recordingHook.closeRecordingModal}
        onCloseModal={recordingHook.closeRecordingModalOnly}
        onUploadRecording={handleRecordingUpload}
        onResetRecording={recordingHook.resetRecording}
        onPlayPreview={recordingHook.playPreview}
        onStopPreview={recordingHook.stopPreview}
        formatRecordingTime={recordingHook.formatTime}
        progressPercent={recordingHook.getProgressPercent()}
        showTitleInput={false}
        closeOnOutsideClick={true}
      />
      
      {/* 텍스트 추출 중 로딩 스피너 */}
      {isExtractingText && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10000,
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
        }}>
          <Spinner style={{ width: '24px', height: '24px' }} />
          <span style={{ fontSize: '14px', color: '#666' }}>텍스트 추출 중...</span>
        </div>
      )}
      </PanelContent>
    </Panel>
  );
};

export default AILearningPanel;

